import { getAuth } from 'firebase/auth';
import { reserveStockPatch } from '../utils/productVariants';
import WholesaleBreakdown from "../components/WholesaleBreakdown";
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  FiCheckCircle,
  FiClock,
  FiEye,
  FiMessageCircle,
  FiPackage,
  FiSearch,
  FiUser,
  FiXCircle,
} from "react-icons/fi";
import {
  readAffiliateCommissionByLevel,
  readAffiliateTotalCommission,
} from "../utils/affiliateCommission";
import { useAuth } from "../AuthProvider";
import { showToast } from "../utils/toast";

const UI_CURRENCY = "LKR";

function money(n, currency = UI_CURRENCY) {
  return `${currency} ${Number(n || 0).toLocaleString("en-LK")}`;
}

function formatDate(ts) {
  const value = ts?.toDate?.() || ts;
  const date = value instanceof Date ? value : value ? new Date(value) : null;
  return date ? date.toLocaleString() : "";
}

function titleOf(order = {}) {
  return (
    order.courseTitle ||
    order.product?.title ||
    order.serviceTitle ||
    order.itemTitle ||
    "Untitled item"
  );
}

function typeOf(order = {}) {
  return (
    order.kind ||
    order.type ||
    (order.courseId ? "course" : "") ||
    (order.productId || order.product?.id ? "product" : "") ||
    (order.serviceId ? "service" : "") ||
    "order"
  );
}

function slipUrlOf(order = {}) {
  return order.payment?.slipUrl || order.slipUrl || "";
}

function slipKindOf(url = "") {
  return String(url || "").toLowerCase().includes(".pdf") ? "pdf" : "image";
}

function roundMoney(value) {
  return Math.round(Number(value || 0) * 100) / 100;
}

function monthKeyNow() {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function affiliateLevelMapOf(order = {}) {
  return readAffiliateCommissionByLevel(order);
}

async function getReferralUserById(uid) {
  if (!uid) return null;
  const snap = await getDocs(
    query(collection(db, "referral_links"), where("ownerUserId", "==", uid), limit(1)),
  ).catch(() => null);
  if (!snap || snap.empty) return null;
  const row = snap.docs[0];
  return { id: row.data()?.ownerUserId || row.id, ...row.data() };
}

async function getReferralUserByCode(code) {
  if (!code) return null;
  const snap = await getDoc(doc(db, "referral_links", code)).catch(() => null);
  return snap?.exists?.()
    ? { id: snap.data()?.ownerUserId || snap.id, ...snap.data() }
    : null;
}

async function resolveAffiliateChain(order = {}) {
  const first =
    (await getReferralUserById(order.affiliateReferrerUid || order.referrerUserId)) ||
    (await getReferralUserByCode(order.affiliateReferralCode || order.referralCode));
  if (!first) return [];

  const chain = [first];
  let cursor = first;
  while (chain.length < 3 && cursor?.referredBy) {
    const next = await getReferralUserByCode(cursor.referredBy);
    if (!next || chain.some((row) => row?.id === next.id)) break;
    chain.push(next);
    cursor = next;
  }
  return chain;
}

function buildAffiliatePayouts(order = {}, chain = []) {
  const byLevel = affiliateLevelMapOf(order);
  const configuredTotal = roundMoney(readAffiliateTotalCommission(order));
  const seen = new Set();
  const payouts = [];
  const unassignedBreakdown = [];

  [0, 1, 2].forEach((index) => {
    const user = chain[index];
    const chainPosition = index + 1;
    const amount = roundMoney(byLevel[chainPosition]);
    if (!(amount > 0)) return;

    if (!user?.id) {
      unassignedBreakdown.push({
        chainPosition,
        amount,
        label: `Missing upline level ${chainPosition}`,
      });
      return;
    }
    if (!user?.id || seen.has(user.id)) return;
    seen.add(user.id);

    payouts.push({
      userId: user.id,
      level: chainPosition,
      chainPosition,
      userLevel: Math.max(1, Math.min(3, Number(user.level || chainPosition) || chainPosition)),
      amount,
      referralCode: user.referralCode || "",
      name: user.fullName || user.displayName || user.email || user.id,
    });
  });

  const assignedAmount = roundMoney(
    payouts.reduce((sum, entry) => sum + Number(entry.amount || 0), 0),
  );
  const unassignedAmount = roundMoney(
    unassignedBreakdown.reduce((sum, entry) => sum + Number(entry.amount || 0), 0),
  );

  return {
    payouts,
    configuredTotal,
    assignedAmount,
    unassignedAmount,
    unassignedBreakdown,
  };
}

function getOrderedProductLines(order = {}) {
  const source =
    Array.isArray(order.productItems) && order.productItems.length > 0
      ? order.productItems
      : order?.product?.id || order?.productId
        ? [
            {
              id: order?.product?.id || order?.productId || "",
              title: order?.product?.title || "",
              qty: Number(order?.product?.qty || 1),
            },
          ]
        : [];

  const merged = new Map();
  source.forEach((item) => {
    const id = String(item?.id || item?.productId || "").trim();
    if (!id) return;
    const qty = Math.max(1, Number(item?.qty || item?.quantity || 1) || 1);
    const key = `${id}::${item.variantId || ""}`;
    const prev = merged.get(key) || { id, title: item?.title || "", qty: 0 };
    merged.set(key, {
      variantId: item.variantId || "",
      id,
      title: prev.title || item?.title || "",
      qty: prev.qty + qty,
    });
  });
  return Array.from(merged.values());
}

function getAffiliateQuantityMultiplier(order = {}) {
  const kind = typeOf(order);
  if (kind !== "product") return 1;
  const lines = getOrderedProductLines(order);
  const totalQty = lines.reduce(
    (sum, line) => sum + Math.max(1, Number(line.qty || 1) || 1),
    0,
  );
  return Math.max(1, totalQty || Number(order?.product?.qty || 1) || 1);
}

function scaleAffiliateSnapshot(order = {}, multiplier = 1) {
  const qtyMultiplier = Math.max(1, Number(multiplier || 1) || 1);
  if (qtyMultiplier === 1) return order;

  const scaledByLevel = affiliateLevelMapOf(order);
  const level1 = roundMoney(scaledByLevel[1] * qtyMultiplier);
  const level2 = roundMoney(scaledByLevel[2] * qtyMultiplier);
  const level3 = roundMoney(scaledByLevel[3] * qtyMultiplier);
  const total = roundMoney((level1 || 0) + (level2 || 0) + (level3 || 0));

  return {
    ...order,
    affiliateCommissionAmount: total,
    affiliateLevel1Amount: level1,
    affiliateLevel2Amount: level2,
    affiliateLevel3Amount: level3,
    affiliateCommissionByLevel: { 1: level1, 2: level2, 3: level3 },
    affiliateTotalCommissionAmount: total,
  };
}

async function reserveApprovedOrderStock(order = {}) {
  if (!["product","product_cart"].includes(typeOf(order))) return;
  const lines = getOrderedProductLines(order);
  if (!lines.length) return;
  await runTransaction(db, async transaction => {
    const orderRef=doc(db,'orders',order.id);
    const orderSnapshot=await transaction.get(orderRef);
    if(!orderSnapshot.exists())throw new Error('Order is no longer available.');
    const fresh=orderSnapshot.data();
    if(fresh.payment?.stockReserved || fresh.stockReservationStatus==='reserved')return;
    if(fresh.status!=='pending')throw new Error('Only pending orders can reserve stock.');
    const lines = getOrderedProductLines(fresh);
    const ids=[...new Set(lines.map(line=>line.id))];
    // Read every document before writing; multiple variants share one product.
    const products=await Promise.all(ids.map(id=>transaction.get(doc(db,'courses',id))));
    const patches=products.map((snapshot,index)=>{
      if(!snapshot.exists())throw new Error('An ordered product is no longer available.');
      return {ref:doc(db,'courses',ids[index]),patch:reserveStockPatch(snapshot.data(),lines.filter(line=>line.id===ids[index]))};
    });
    for(const {ref,patch} of patches) if(Object.keys(patch).length)transaction.set(ref,{...patch,updatedAt:serverTimestamp()},{merge:true});
    transaction.update(orderRef,{payment:{...(fresh.payment||{}),stockReserved:true}, sellerReviewedBy:getAuth().currentUser.uid, reviewedByRole:'instructor'});
  });
}

function buyerUidOf(order = {}) {
  return order.userId || order.buyerUid || order.buyerId || null;
}

function normalizeWhatsappPhone(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.startsWith("+")) {
    return `+${raw.slice(1).replace(/\D/g, "")}`;
  }
  return raw.replace(/\D/g, "");
}

function whatsappHref(phone = "", message = "") {
  const normalized = normalizeWhatsappPhone(phone);
  if (!normalized) return "";
  const digits = normalized.startsWith("+") ? normalized.slice(1) : normalized;
  const encoded = encodeURIComponent(message || "");
  return `https://wa.me/${digits}${encoded ? `?text=${encoded}` : ""}`;
}

function buildBuyerWhatsappMessage(order = {}) {
  const total = money(
    order.amountDetail?.total || order.amount || 0,
    order.amountDetail?.currency || order.currency || UI_CURRENCY,
  );
  return [
    `Hello ${order.buyer?.name || order.buyerName || "there"},`,
    "",
    `This is an update about your Ceylon Web Mall order.`,
    `Order ID: ${order.id || "-"}`,
    `Item: ${titleOf(order)}`,
    `Total: ${total}`,
    "",
    `Please reply here if you need anything else.`,
  ].join("\n");
}

function statusTone(status = "") {
  const value = String(status || "").toLowerCase();
  if (value === "paid" || value === "completed") {
    return "bg-green-100 text-green-700";
  }
  if (value === "processing") {
    return "bg-indigo-100 text-indigo-700";
  }
  if (value === "shipped") {
    return "bg-cyan-100 text-cyan-700";
  }
  if (value === "declined") {
    return "bg-rose-100 text-rose-700";
  }
  if (value === "submitted" || value === "pending") {
    return "bg-amber-100 text-amber-800";
  }
  return "bg-gray-100 text-gray-700";
}

function Modal({ open, onClose, children, max = "max-w-3xl" }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className={`w-full ${max} rounded-3xl border bg-white shadow-2xl overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

async function notifyBuyer(buyerUid, payload) {
  if (!buyerUid) return;
  await addDoc(collection(db, "users", buyerUid, "notifications"), {
    userId: buyerUid,
    recipientUid: buyerUid,
    ...payload,
    createdAt: serverTimestamp(),
    read: false,
    seen: false,
  });
}

async function notifyAffiliateUser(affiliateUid, payload) {
  if (!affiliateUid) return;
  await addDoc(collection(db, "referral_users", affiliateUid, "notifications"), {
    userId: affiliateUid,
    recipientUid: affiliateUid,
    read: false,
    seen: false,
    ...payload,
    createdAt: serverTimestamp(),
  });
}

export default function SalesOrders() {
  const { user, authLoading } = useAuth();
  const uid = user?.uid || "";

  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [slipViewer, setSlipViewer] = useState({
    open: false,
    url: "",
    kind: "image",
    title: "",
  });
  const [approveState, setApproveState] = useState({
    open: false,
    row: null,
    busy: false,
  });
  const [statusActionState, setStatusActionState] = useState({
    orderId: "",
    status: "",
    busy: false,
  });
  const [trackingDrafts, setTrackingDrafts] = useState({});
  const [trackingState, setTrackingState] = useState({
    orderId: "",
    busy: false,
  });
  const [declineState, setDeclineState] = useState({
    open: false,
    row: null,
    reason: "",
    busy: false,
  });

  useEffect(() => {
    if (!uid) return;

    const bySeller = new Map();
    const unsubscribers = [
      onSnapshot(
        query(
          collection(db, "orders"),
          where("sellerUserId", "==", uid),
          orderBy("createdAt", "desc"),
        ),
        (snap) => {
          snap.docs.forEach((d) => bySeller.set(d.id, { id: d.id, ...d.data() }));
          setRows(
            Array.from(bySeller.values()).sort(
              (a, b) =>
                (b.createdAt?.toMillis?.() || 0) -
                (a.createdAt?.toMillis?.() || 0),
            ),
          );
        },
      ),
      onSnapshot(
        query(
          collection(db, "orders"),
          where("instructorUserId", "==", uid),
          orderBy("createdAt", "desc"),
        ),
        (snap) => {
          snap.docs.forEach((d) => bySeller.set(d.id, { id: d.id, ...d.data() }));
          setRows(
            Array.from(bySeller.values()).sort(
              (a, b) =>
                (b.createdAt?.toMillis?.() || 0) -
                (a.createdAt?.toMillis?.() || 0),
            ),
          );
        },
      ),
    ];

    return () => unsubscribers.forEach((unsub) => unsub && unsub());
  }, [uid]);

  useEffect(() => {
    setTrackingDrafts((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        if (
          Object.prototype.hasOwnProperty.call(next, row.id) === false &&
          row?.trackingNumber
        ) {
          next[row.id] = row.trackingNumber;
        }
      });
      return next;
    });
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      const rowStatus = String(row.status || "").toLowerCase();
      if (status !== "all" && rowStatus !== status) return false;
      if (!q) return true;
      return [
        row.id,
        titleOf(row),
        typeOf(row),
        row.buyer?.name,
        row.buyerName,
        row.buyer?.phone,
        row.buyerPhone,
        row.buyer?.email,
        row.buyerEmail,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [rows, search, status]);

  function isPhysicalOrder(row = {}) {
    const kind = typeOf(row);
    if (kind === "product" || kind === "product_cart") {
      return true;
    }
    if (row?.product?.isPhysical === true) return true;
    if (Array.isArray(row?.productItems) && row.productItems.length > 0) {
      return row.productItems.every((item) => item?.isPhysical === true);
    }
    return false;
  }

  function getNextStatusAction(row = {}) {
    const current = String(row.status || "").toLowerCase();
    const physical = isPhysicalOrder(row);

    if (current === "paid") {
      return physical
        ? { status: "processing", label: "Mark Processing" }
        : { status: "completed", label: "Mark Completed" };
    }
    if (physical && current === "processing") {
      return { status: "shipped", label: "Mark Shipped" };
    }
    if (physical && current === "shipped") {
      return { status: "completed", label: "Mark Completed" };
    }
    return null;
  }

  async function updateFulfillmentStatus(row, nextStatus) {
    if (!row?.id || !nextStatus || statusActionState.busy) return;
    setStatusActionState({ orderId: row.id, status: nextStatus, busy: true });

    const statusMessages = {
      processing: {
        title: "Order is processing",
        body: `Your order for "${titleOf(row)}" is now being prepared by the shop owner.`,
      },
      shipped: {
        title: "Order shipped",
        body: `Your order for "${titleOf(row)}" has been marked as shipped by the shop owner.${
          String(row?.trackingNumber || "").trim()
            ? ` Tracking number: ${String(row.trackingNumber).trim()}.`
            : ""
        }`,
      },
      completed: {
        title: "Order completed",
        body: `Your order for "${titleOf(row)}" is marked as completed.`,
      },
    };

    try {
      await updateDoc(doc(db, "orders", row.id), {
        status: nextStatus,
        sellerReviewedAt: serverTimestamp(),
        sellerReviewedBy: uid,
        reviewedByRole: "instructor",
      });

      await notifyBuyer(buyerUidOf(row), {
        title: statusMessages[nextStatus]?.title || "Order updated",
        body:
          statusMessages[nextStatus]?.body ||
          `Your order for "${titleOf(row)}" was updated.`,
        type: nextStatus === "completed" ? "success" : "info",
        orderId: row.id,
        courseId: row.courseId || null,
        productId: row.productId || row.product?.id || null,
        serviceId: row.serviceId || null,
      });

      showToast(
        nextStatus === "completed"
          ? "Order marked as completed."
          : `Order marked as ${nextStatus}.`,
        { type: "success" },
      );
    } catch (error) {
      console.error(error);
      showToast("Failed to update the order status.", { type: "error" });
    } finally {
      setStatusActionState({ orderId: "", status: "", busy: false });
    }
  }

  async function saveTrackingNumber(row) {
    const trackingNumber = String(trackingDrafts[row?.id] || "").trim();
    if (!row?.id || trackingState.busy) return;

    setTrackingState({ orderId: row.id, busy: true });
    try {
      await updateDoc(doc(db, "orders", row.id), {
        trackingNumber: trackingNumber || null,
        trackingUpdatedAt: serverTimestamp(),
        trackingUpdatedBy: uid,
      });

      if (trackingNumber) {
        await notifyBuyer(buyerUidOf(row), {
          title: "Tracking number updated",
          body: `A tracking number was added for "${titleOf(row)}": ${trackingNumber}.`,
          type: "info",
          orderId: row.id,
          courseId: row.courseId || null,
          productId: row.productId || row.product?.id || null,
          serviceId: row.serviceId || null,
        });
      }

      showToast(
        trackingNumber
          ? "Tracking number saved."
          : "Tracking number cleared.",
        { type: "success" },
      );
    } catch (error) {
      console.error(error);
      showToast("Failed to save tracking number.", { type: "error" });
    } finally {
      setTrackingState({ orderId: "", busy: false });
    }
  }

  async function approveOrder(row) {
    if (!row?.id || approveState.busy) return;
    setApproveState((prev) => ({ ...prev, busy: true }));
    try {
      const orderRef = doc(db, "orders", row.id);
      const batch = writeBatch(db);
      const kind = typeOf(row);
      const buyerUid = buyerUidOf(row);
      const qtyMultiplier = getAffiliateQuantityMultiplier(row);
      const affiliateOrder = scaleAffiliateSnapshot(row, qtyMultiplier);
      const affiliateChain = await resolveAffiliateChain(affiliateOrder);
      const affiliateResult =
        affiliateOrder.affiliateEnabled === true
          ? buildAffiliatePayouts(affiliateOrder, affiliateChain)
          : {
              payouts: [],
              configuredTotal: 0,
              assignedAmount: 0,
              unassignedAmount: 0,
              unassignedBreakdown: [],
            };
      const affiliateLevelPayouts = affiliateResult.payouts;
      const payoutMonthKey = monthKeyNow();

      await reserveApprovedOrderStock(row);

      batch.update(orderRef, {
        status: "paid",
        approvedAt: serverTimestamp(),
        sellerReviewedAt: serverTimestamp(),
        sellerReviewedBy: uid,
        reviewedByRole: "instructor",
        sellerUserId: uid,
        instructorUserId: uid,
        declineReason: null,
        affiliateUplineChain: affiliateChain.map((entry, index) => ({
          chainPosition: index + 1,
          userId: entry.id,
          referralCode: entry.referralCode || "",
          level: Number(entry.level || index + 1) || index + 1,
          name: entry.fullName || entry.displayName || entry.email || entry.id,
        })),
        affiliateLevelPayouts,
        affiliateTotalCommissionAmount: affiliateResult.configuredTotal,
        "payment.reviewed": true,
        "payment.declineReason": null,
      });

      affiliateLevelPayouts.forEach((entry) => {
        const payoutRef = doc(collection(db, "payouts"));
        batch.set(payoutRef, {
          userId: entry.userId,
          instructorUid: uid,
          orderId: row.id,
          courseId: row.courseId || null,
          productId: row.productId || row.product?.id || null,
          serviceId: row.serviceId || null,
          itemType: kind,
          itemTitle: titleOf(row),
          amount: entry.amount,
          level: entry.level,
          chainPosition: entry.chainPosition,
          currency: row.amountDetail?.currency || row.currency || UI_CURRENCY,
          status: "due",
          source: "affiliate_sale",
          sourceType: `${kind === "product_cart" ? "product" : kind}_sale_affiliate`,
          monthKey: payoutMonthKey,
          affiliateReferralCode: entry.referralCode || "",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });

      if (kind === "course" && buyerUid && row.courseId) {
        batch.set(
          doc(db, "enrollments", `${buyerUid}_${row.courseId}`),
          {
            userId: buyerUid,
            courseId: row.courseId,
            enrolledAt: serverTimestamp(),
            source: "seller_bank_manual",
            orderId: row.id,
          },
          { merge: true },
        );
      }

      await batch.commit();

      await Promise.all(
        affiliateLevelPayouts.map((entry) =>
          notifyAffiliateUser(entry.userId, {
            type: "commission",
            status: "due",
            orderId: row.id,
            courseId: row.courseId || null,
            productId: row.productId || row.product?.id || null,
            serviceId: row.serviceId || null,
            level: entry.level,
            amount: entry.amount,
            source: "affiliate_sale",
            message: `A sale in your network was approved for "${titleOf(row)}". You earned ${money(
              entry.amount,
              row.amountDetail?.currency || row.currency || UI_CURRENCY,
            )} at Level ${entry.level}.`,
          }),
        ),
      );

      await notifyBuyer(buyerUid, {
        title: "Payment approved",
        body:
          kind === "course"
            ? `Your payment for "${titleOf(row)}" was approved. You can start learning now.`
            : `Your payment for "${titleOf(row)}" was approved by the seller.`,
        type: "success",
        orderId: row.id,
        courseId: row.courseId || null,
        productId: row.productId || row.product?.id || null,
        serviceId: row.serviceId || null,
      });

      setApproveState({ open: false, row: null, busy: false });
    } catch (error) {
      console.error(error);
      showToast("Failed to approve this payment.", { type: "error" });
      setApproveState((prev) => ({ ...prev, busy: false }));
    }
  }

  async function declineOrder() {
    const row = declineState.row;
    const reason = declineState.reason.trim();
    if (!row?.id || !reason || declineState.busy) return;

    setDeclineState((prev) => ({ ...prev, busy: true }));
    try {
      await updateDoc(doc(db, "orders", row.id), {
        status: "declined",
        declineReason: reason,
        declinedAt: serverTimestamp(),
        sellerReviewedAt: serverTimestamp(),
        sellerReviewedBy: uid,
        reviewedByRole: "instructor",
        "payment.reviewed": true,
        "payment.declineReason": reason,
      });

      await notifyBuyer(buyerUidOf(row), {
        title: "Payment declined",
        body: `Your payment for "${titleOf(row)}" was declined. Reason: ${reason}.`,
        type: "error",
        orderId: row.id,
        courseId: row.courseId || null,
        productId: row.productId || row.product?.id || null,
        serviceId: row.serviceId || null,
      });

      setDeclineState({ open: false, row: null, reason: "", busy: false });
    } catch (error) {
      console.error(error);
      showToast("Failed to decline this payment.", { type: "error" });
      setDeclineState((prev) => ({ ...prev, busy: false }));
    }
  }

  return (
    <>
      {authLoading ? (
        <div className="max-w-6xl mx-auto px-4 py-12 text-center text-gray-500">
          Loading sales orders...
        </div>
      ) : (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Sales Orders</h1>
            <p className="mt-1 text-gray-600">
              Review buyer slips for your courses, products, and services.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <FiSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search order, buyer, item..."
                className="rounded-xl border py-2.5 pl-9 pr-3 text-sm"
              />
            </div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="rounded-xl border px-3 py-2.5 text-sm"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="submitted">Submitted</option>
              <option value="paid">Paid</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="completed">Completed</option>
              <option value="declined">Declined</option>
            </select>
          </div>
        </div>

        <div className="rounded-3xl border bg-white shadow-sm overflow-hidden">
          <div className="border-b bg-gray-50 px-5 py-4 font-semibold">
            Orders ({filtered.length})
          </div>

          {filtered.length === 0 ? (
            <div className="px-5 py-12 text-center text-gray-500">
              No matching orders yet.
            </div>
          ) : (
            <div className="divide-y">
              {filtered.map((row) => {
                const slipUrl = slipUrlOf(row);
                const payment = row.sellerPaymentSnapshot || {};
                const pendingReview =
                  ["submitted", "pending"].includes(
                    String(row.status || "").toLowerCase(),
                  ) && !!slipUrl;
                const nextStatusAction = getNextStatusAction(row);
                const statusBusy =
                  statusActionState.busy && statusActionState.orderId === row.id;
                const trackingBusy =
                  trackingState.busy && trackingState.orderId === row.id;
                const buyerPhone =
                  row.buyer?.whatsapp ||
                  row.buyerWhatsapp ||
                  row.buyer?.phone ||
                  row.buyerPhone ||
                  row.buyer?.phoneAlt ||
                  "";
                const buyerWhatsappLink = whatsappHref(
                  buyerPhone,
                  buildBuyerWhatsappMessage(row),
                );
                const trackingDraft =
                  trackingDrafts[row.id] ??
                  (row.trackingNumber ? String(row.trackingNumber) : "");

                return (
                  <div
                    key={row.id}
                    className="grid gap-4 px-5 py-4 lg:grid-cols-[1.1fr,0.9fr,auto]"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-semibold">{titleOf(row)}</div>
                        <span className="rounded-full bg-blue-50 px-2 py-1 text-xs font-semibold uppercase text-blue-700">
                          {typeOf(row)}
                        </span>
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-semibold ${statusTone(
                            row.status,
                          )}`}
                        >
                          {row.status || "pending"}
                        </span>
                      </div>

                      <div className="mt-2 text-sm text-gray-600">
                        Order: <span className="font-mono">{row.id}</span>
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        Amount:{" "}
                        <span className="font-semibold">
                          {money(
                            row.amountDetail?.total || row.amount || 0,
                            row.amountDetail?.currency || row.currency || UI_CURRENCY,
                          )}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        Submitted: {formatDate(row.submittedAt || row.createdAt)}
                      </div>

                      <div className="mt-3 rounded-2xl border bg-gray-50 p-3 text-sm">
                        <div className="font-medium text-gray-900">Buyer</div>
                        <div className="mt-1 flex items-center gap-2 text-gray-700">
                          <FiUser className="text-gray-400" />
                          {row.buyer?.name || row.buyerName || "Unknown buyer"}
                        </div>
                        {row.buyer?.phone || row.buyerPhone ? (
                          <div className="mt-1 text-gray-600">
                            {row.buyer?.phone || row.buyerPhone}
                          </div>
                        ) : null}
                        {row.buyer?.email || row.buyerEmail ? (
                          <div className="mt-1 text-gray-600">
                            {row.buyer?.email || row.buyerEmail}
                          </div>
                        ) : null}
                        {buyerWhatsappLink ? (
                          <a
                            href={buyerWhatsappLink}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-3 inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 font-medium text-emerald-700 transition hover:bg-emerald-100"
                          >
                            <FiMessageCircle />
                            Message on WhatsApp
                          </a>
                        ) : null}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="rounded-2xl border bg-gray-50 p-3 text-sm">
                        <div className="font-medium text-gray-900">
                          Payment destination
                        </div>
                        <div className="mt-2 text-gray-700">
                          {payment.bankName || "Not saved on order"}
                        </div>
                        <div className="mt-1 text-gray-600">
                          {payment.accountHolder || "-"}
                        </div>
                        <div className="mt-1 font-medium text-gray-900">
                          {payment.accountNumber || "-"}
                        </div>
                        <div className="mt-1 text-gray-600">
                          Branch: {payment.branch || "-"}
                        </div>
                        {payment.instructions ? (
                          <div className="mt-2 text-xs text-gray-600">
                            {payment.instructions}
                          </div>
                        ) : null}
                      </div>

                      <div className="rounded-2xl border bg-gray-50 p-3 text-sm">
                        <WholesaleBreakdown rows={row.wholesaleBreakdown} />
                        <div className="font-medium text-gray-900">Slip</div>
                        {slipUrl ? (
                          <button
                            type="button"
                            onClick={() =>
                              setSlipViewer({
                                open: true,
                                url: slipUrl,
                                kind: slipKindOf(slipUrl),
                                title: titleOf(row),
                              })
                            }
                            className="mt-2 inline-flex items-center gap-2 rounded-xl border bg-white px-3 py-2 text-blue-600 hover:bg-blue-50"
                          >
                            <FiEye /> Open slip
                          </button>
                        ) : (
                          <div className="mt-2 text-gray-500">No slip yet</div>
                        )}

                        {row.declineReason || row.payment?.declineReason ? (
                          <div className="mt-2 rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700">
                            {row.declineReason || row.payment?.declineReason}
                          </div>
                        ) : null}
                      </div>

                      {isPhysicalOrder(row) ? (
                        <div className="rounded-2xl border bg-gray-50 p-3 text-sm">
                          <div className="flex items-center gap-2 font-medium text-gray-900">
                            <FiPackage className="text-gray-500" />
                            Tracking number
                          </div>
                          <div className="mt-2 flex gap-2">
                            <input
                              type="text"
                              value={trackingDraft}
                              onChange={(e) =>
                                setTrackingDrafts((prev) => ({
                                  ...prev,
                                  [row.id]: e.target.value,
                                }))
                              }
                              placeholder="Add tracking number"
                              className="min-w-0 flex-1 rounded-xl border bg-white px-3 py-2 outline-none transition focus:border-blue-500"
                            />
                            <button
                              type="button"
                              onClick={() => saveTrackingNumber(row)}
                              disabled={trackingBusy}
                              className="rounded-xl border border-blue-200 bg-white px-3 py-2 font-medium text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {trackingBusy ? "Saving..." : "Save"}
                            </button>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            This will also appear in the buyer&apos;s order page.
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-col gap-2 lg:items-end">
                      {pendingReview ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setApproveState({ open: true, row, busy: false })}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-white hover:bg-green-700"
                          >
                            <FiCheckCircle /> Approve
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setDeclineState({
                                open: true,
                                row,
                                reason: "",
                                busy: false,
                              })
                            }
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-200 px-4 py-2 text-rose-700 hover:bg-rose-50"
                          >
                            <FiXCircle /> Decline
                          </button>
                        </>
                      ) : nextStatusAction ? (
                        <button
                          type="button"
                          onClick={() =>
                            updateFulfillmentStatus(row, nextStatusAction.status)
                          }
                          disabled={statusBusy}
                          className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:bg-blue-300"
                        >
                          <FiCheckCircle />
                          {statusBusy
                            ? "Updating..."
                            : nextStatusAction.label}
                        </button>
                      ) : (
                        <div className="inline-flex items-center gap-2 rounded-xl bg-gray-100 px-3 py-2 text-sm text-gray-600">
                          <FiClock /> No action needed
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      )}

      <Modal
        open={slipViewer.open}
        onClose={() =>
          setSlipViewer({ open: false, url: "", kind: "image", title: "" })
        }
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-bold">Payment Slip</div>
            <div className="mt-1 text-sm text-gray-500">{slipViewer.title}</div>
          </div>
          <button
            type="button"
            className="rounded-xl border px-3 py-2 text-sm hover:bg-gray-50"
            onClick={() =>
              setSlipViewer({ open: false, url: "", kind: "image", title: "" })
            }
          >
            Close
          </button>
        </div>

        <div className="mt-5">
          {slipViewer.kind === "pdf" ? (
            <iframe
              title="Payment slip"
              src={slipViewer.url}
              className="h-[70vh] w-full rounded-2xl border"
            />
          ) : (
            <img
              src={slipViewer.url}
              alt="Payment slip"
              className="max-h-[70vh] w-full rounded-2xl border object-contain"
            />
          )}
        </div>
      </Modal>

      <Modal
        open={approveState.open}
        onClose={() => setApproveState({ open: false, row: null, busy: false })}
        max="max-w-lg"
      >
        <div className="text-lg font-bold">Approve Payment</div>
        <div className="mt-2 text-sm text-gray-600">
          This will approve the payment for{" "}
          <span className="font-semibold">{titleOf(approveState.row || {})}</span>
          . Course orders will also enroll the buyer automatically.
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-xl border px-4 py-2 hover:bg-gray-50"
            onClick={() =>
              setApproveState({ open: false, row: null, busy: false })
            }
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-xl bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:bg-green-300"
            disabled={approveState.busy}
            onClick={() => approveOrder(approveState.row)}
          >
            {approveState.busy ? "Approving..." : "Approve"}
          </button>
        </div>
      </Modal>

      <Modal
        open={declineState.open}
        onClose={() =>
          setDeclineState({ open: false, row: null, reason: "", busy: false })
        }
        max="max-w-lg"
      >
        <div className="text-lg font-bold">Decline Payment</div>
        <div className="mt-2 text-sm text-gray-600">
          Add a reason so the buyer knows what to fix before re-submitting.
        </div>
        <textarea
          rows={4}
          value={declineState.reason}
          onChange={(e) =>
            setDeclineState((prev) => ({ ...prev, reason: e.target.value }))
          }
          className="mt-4 w-full rounded-2xl border px-4 py-3 outline-none focus:border-blue-500"
          placeholder="Amount mismatch, unreadable slip, wrong reference..."
        />
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-xl border px-4 py-2 hover:bg-gray-50"
            onClick={() =>
              setDeclineState({ open: false, row: null, reason: "", busy: false })
            }
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-xl bg-rose-600 px-4 py-2 text-white hover:bg-rose-700 disabled:bg-rose-300"
            disabled={declineState.busy || !declineState.reason.trim()}
            onClick={declineOrder}
          >
            {declineState.busy ? "Declining..." : "Decline"}
          </button>
        </div>
      </Modal>
    </>
  );
}
